import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, Link2, Pencil, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorMessage } from '@/components/ui/error-message';
import { IvvGateChip } from '@/components/ui/ivv-gate-chip';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import { Spinner } from '@/components/ui/spinner';
import { isIvvGate, useCanFlagIvvGate, useEdlListQuery } from '@/lib/api/deliverables';
import {
  useCheckpointDeliverablesQuery,
  useSetCheckpointDeliverablesMutation,
  useSuggestCheckpointDeliverablesMutation,
  type LinkedDeliverable,
} from '@/lib/api/checkpoints';
import { cn } from '@/lib/utils/cn';
import { formatDay } from './checkpoint-status';

/**
 * The contracted deliverables an IV&V checkpoint depends on — the evidence
 * the checkpoint is assessed against.
 *
 * Each row shows what the scorer needs next to the criteria: the
 * deliverable's workflow status, the AI's reading of the vendor's
 * submission against its acceptance criteria, and open deviations against
 * it. Links come from the AI (confirmed in the review queue) or are set
 * here; `editable` turns on Edit and "Suggest from contract".
 *
 * Contract gates (E19 / T-0218). The editor lists first the vendor
 * milestones the contract names as IV&V gates, each with a one-click Tie
 * for an engagement lead that adds the link through the same PUT, keeping
 * the existing links. A tied gate carries the "IV&V gate" chip in the list.
 */
export function LinkedDeliverablesList({
  items,
  engagementId,
}: {
  items: readonly LinkedDeliverable[];
  engagementId: string;
}) {
  return (
    <ul className="divide-y divide-ink-100 rounded-card border border-ink-100">
      {items.map((d) => (
        <li key={d.register_entry_id} className="px-3 py-2.5 text-body-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/engagements/$engagementId/contract/deliverables"
              params={{ engagementId }}
              className="font-medium text-ink-900 underline-offset-2 hover:underline"
            >
              {d.title}
            </Link>
            {d.is_ivv_gate ? <IvvGateChip /> : null}
            <span className="rounded-chip bg-paper-100 px-2 py-0.5 text-meta text-ink-700">
              {d.status.replace(/_/g, ' ')}
            </span>
            {d.expected_delivery_date ? (
              <span className="text-meta text-ink-500">
                due {formatDay(d.expected_delivery_date)}
              </span>
            ) : null}
            <ProvenanceChip origin={d.source === 'manual' ? 'manual' : 'ai_confirmed'} />
          </div>
          {d.submission_reading ? (
            <p className="mt-1 text-meta text-ink-700">
              AI reading of {d.submission_reading.document_title ?? 'the submission'}:{' '}
              {d.submission_reading.met} met · {d.submission_reading.partially_met} partial ·{' '}
              <span className={cn(d.submission_reading.not_met > 0 && 'text-posture-critical')}>
                {d.submission_reading.not_met} not met
              </span>{' '}
              · {d.submission_reading.not_evidenced} not shown
            </p>
          ) : (
            <p className="mt-1 text-meta text-ink-500">No submission matched yet.</p>
          )}
          {d.open_deviations.length > 0 ? (
            <ul className="mt-1 space-y-0.5">
              {d.open_deviations.map((v) => (
                <li key={v.deviation_entry_id} className="text-meta text-posture-at-risk">
                  Open deviation · {v.severity} · {v.title}
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function LinkedDeliverablesSection({
  engagementId,
  checkpointId,
  editable,
}: {
  engagementId: string;
  checkpointId: string;
  editable: boolean;
}) {
  const links = useCheckpointDeliverablesQuery(checkpointId);
  const [editing, setEditing] = useState(false);
  const suggest = useSuggestCheckpointDeliverablesMutation(checkpointId);
  const items = links.data?.items ?? [];

  if (links.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading…
      </div>
    );
  }
  if (links.error) return <ErrorMessage error={links.error} />;
  if (editing) {
    return (
      <LinkEditor
        engagementId={engagementId}
        checkpointId={checkpointId}
        current={items.map((i) => i.register_entry_id)}
        onDone={() => setEditing(false)}
      />
    );
  }
  return (
    <div className="space-y-2">
      {items.length > 0 ? (
        <LinkedDeliverablesList items={items} engagementId={engagementId} />
      ) : (
        <p className="text-body-sm text-ink-500">
          {links.data?.pending_proposal
            ? 'The AI has proposed deliverables for this checkpoint. Approve them in the review queue.'
            : 'Not linked to any deliverables yet. Without them, scoring cannot use what the vendor submitted.'}
        </p>
      )}
      {editable ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            Edit
          </Button>
          {items.length === 0 && !links.data?.pending_proposal ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={suggest.isPending || suggest.isSuccess}
              onClick={() => suggest.mutate()}
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              {suggest.isSuccess
                ? 'Asked. Proposals will appear in the review queue'
                : 'Suggest from contract'}
            </Button>
          ) : null}
          {suggest.error ? <ErrorMessage error={suggest.error} /> : null}
        </div>
      ) : null}
    </div>
  );
}

function LinkEditor({
  engagementId,
  checkpointId,
  current,
  onDone,
}: {
  engagementId: string;
  checkpointId: string;
  current: string[];
  onDone: () => void;
}) {
  const edl = useEdlListQuery(engagementId, { limit: 500, item_type: 'deliverable' });
  const gatesQuery = useEdlListQuery(engagementId, {
    limit: 500,
    item_type: 'milestone',
    is_ivv_gate: true,
  });
  // An older server ignores the filter; keep only what the chip means.
  const gates = (gatesQuery.data?.entries ?? []).filter(isIvvGate);
  const canTie = useCanFlagIvvGate();
  const save = useSetCheckpointDeliverablesMutation(checkpointId);
  const [selected, setSelected] = useState<Set<string>>(new Set(current));
  const [tying, setTying] = useState<string | null>(null);
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // One click: the gate joins what is linked now; nothing else changes.
  const tie = (id: string) => {
    setTying(id);
    save.mutate(Array.from(new Set([...selected, id])), {
      onSuccess: () => setSelected((prev) => new Set(prev).add(id)),
      onSettled: () => setTying(null),
    });
  };
  return (
    <div className="space-y-2">
      {gates.length > 0 ? (
        <div>
          <p className="mb-1 text-meta font-semibold text-ink-700">Contract gates</p>
          <ul
            aria-label="Contract gates"
            className="divide-y divide-ink-100 rounded-card border border-ink-100"
          >
            {gates.map((g) => (
              <li
                key={g.register_entry_id}
                className="flex items-center gap-2 px-3 py-2 text-body-sm"
              >
                <IvvGateChip />
                <span className="min-w-0 flex-1 truncate text-ink-900">{g.title}</span>
                {g.sow_ref ? <span className="text-meta text-ink-500">{g.sow_ref}</span> : null}
                {selected.has(g.register_entry_id) ? (
                  <span className="inline-flex items-center gap-1 text-meta text-ink-500">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    Tied
                  </span>
                ) : canTie ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-label={`Tie ${g.title}`}
                    disabled={save.isPending}
                    onClick={() => tie(g.register_entry_id)}
                  >
                    {tying === g.register_entry_id ? (
                      <Spinner />
                    ) : (
                      <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Tie
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {edl.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading deliverables…
        </div>
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto rounded-card border border-ink-100 p-2">
          {(edl.data?.entries ?? []).map((e) => (
            <li key={e.register_entry_id}>
              <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-body-sm hover:bg-paper-50">
                <input
                  type="checkbox"
                  checked={selected.has(e.register_entry_id)}
                  onChange={() => toggle(e.register_entry_id)}
                />
                <span className="text-ink-900">{e.title}</span>
                {e.sow_ref ? <span className="text-meta text-ink-500">{e.sow_ref}</span> : null}
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={save.isPending}
          onClick={() => save.mutate(Array.from(selected), { onSuccess: onDone })}
        >
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          Save {selected.size} {selected.size === 1 ? 'deliverable' : 'deliverables'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onDone}>
          <X className="h-3.5 w-3.5" aria-hidden="true" />
          Cancel
        </Button>
        <Link2 className="h-3.5 w-3.5 text-ink-300" aria-hidden="true" />
      </div>
      {save.error ? <ErrorMessage error={save.error} /> : null}
    </div>
  );
}
